/**
 * Le réglage d'apparence de la console — lot 2.5.
 *
 * ─── Pourquoi la console, et pourquoi maintenant ────────────────────────────
 *
 * La file du tuteur est l'outil du SOIR : « Ma journée » se traite après le
 * souper, souvent sur un portable, dans une pièce peu éclairée. Le mobile a son
 * mode sombre depuis le Sprint 18 ; la console est restée en clair.
 *
 * ─── Trois décisions, et leur raison ────────────────────────────────────────
 *
 * 1. **Le mode « Automatique » est résolu ICI, en JavaScript**, et le DOM ne
 *    porte jamais que `clair` ou `sombre`. L'alternative — une seconde copie de
 *    la palette sous `@media (prefers-color-scheme: dark)` — obligerait à
 *    maintenir deux exemplaires des mêmes soixante valeurs. Deux copies d'une
 *    palette finissent toujours par diverger : c'est le défaut que ce lot
 *    corrige, on ne va pas le réintroduire en le corrigeant.
 *
 * 2. **La préférence est écrite AVANT le premier rendu React** (`appliquer()`
 *    est appelé depuis `main.tsx`, au chargement du module). Sans ça, la
 *    console peindrait un écran clair pendant un cadre avant de basculer — le
 *    « flash blanc » que tout tuteur travaillant le soir remarquerait.
 *
 * 3. **`localStorage`, pas le serveur.** Même arbitrage que côté mobile
 *    (décision fondateur du 2026-08-30, persistance locale) : une préférence
 *    d'apparence n'est pas une donnée de compte, elle est propre au poste. Un
 *    tuteur qui travaille le soir chez lui et le jour au bureau ne veut pas le
 *    même réglage aux deux endroits.
 *
 * Module PUR côté logique : les fonctions de résolution et de lecture se
 * testent sans DOM ni React.
 */
import type { ModeApparence, ModeResolu } from '@silex/design-system';

export type { ModeApparence, ModeResolu };

/** Clé de stockage. Versionnée, comme celle du mobile (`silex.apparence.v1`). */
export const CLE_APPARENCE_CONSOLE = 'silex.console.apparence.v1';

/**
 * Défaut : `systeme`. Même raisonnement que côté mobile — c'est le seul défaut
 * cohérent avec le reste de la machine (barre de défilement, champs natifs,
 * menus du navigateur suivent déjà `color-scheme`), et « Clair » reste à une
 * touche.
 */
export const MODE_PAR_DEFAUT: ModeApparence = 'systeme';

const MODES: readonly ModeApparence[] = ['clair', 'sombre', 'systeme'];

export function estModeApparence(valeur: unknown): valeur is ModeApparence {
  return typeof valeur === 'string' && (MODES as readonly string[]).includes(valeur);
}

/**
 * Résout `systeme` avec le schéma de couleurs du système. FONCTION PURE : le
 * schéma est passé en argument, jamais lu de `matchMedia` ici — c'est ce qui
 * la rend testable, et c'est le même contrat que `resolveMode` du design
 * system côté mobile.
 */
export function resoudreMode(
  mode: ModeApparence,
  schemaSysteme: 'light' | 'dark' | null,
): ModeResolu {
  if (mode === 'systeme') return schemaSysteme === 'dark' ? 'sombre' : 'clair';
  return mode;
}

/** `localStorage` s'il est réellement utilisable, `null` sinon. Ne lève jamais. */
function stockage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    if (!s) return null;
    const sonde = `${CLE_APPARENCE_CONSOLE}.sonde`;
    s.setItem(sonde, '1');
    s.removeItem(sonde);
    return s;
  } catch {
    return null;
  }
}

/**
 * Repli mémoire, pour les seuls navigateurs qui REFUSENT le stockage
 * (navigation privée, témoins bloqués, quota). Le réglage s'applique alors et
 * ne survit pas au rechargement — dégradation, pas panne.
 */
let memoire: ModeApparence | null = null;

/**
 * Lit la préférence enregistrée. Toute valeur inconnue retombe sur le défaut.
 *
 * QUAND LE STOCKAGE FONCTIONNE, IL FAIT AUTORITÉ — y compris s'il contient une
 * valeur illisible, auquel cas on retombe sur le défaut plutôt que sur ce que
 * la mémoire du chargement courant croit savoir. Sans cette règle, un réglage
 * effacé depuis un autre onglet réapparaîtrait ici : deux sources de vérité
 * pour le même réglage, et l'une des deux finit toujours par mentir.
 */
export function lireMode(): ModeApparence {
  const s = stockage();
  if (s) {
    try {
      const lu = s.getItem(CLE_APPARENCE_CONSOLE);
      return estModeApparence(lu) ? lu : MODE_PAR_DEFAUT;
    } catch {
      // Stockage refusé en cours de route : on retombe sur la mémoire.
    }
  }
  return memoire ?? MODE_PAR_DEFAUT;
}

/** Écrit la préférence. */
export function ecrireMode(mode: ModeApparence): void {
  memoire = mode;
  const s = stockage();
  try {
    s?.setItem(CLE_APPARENCE_CONSOLE, mode);
  } catch {
    // Quota ou stockage refusé : la mémoire ci-dessus tient le réglage.
  }
}

/** Le schéma rapporté par le système, ou `null` si le navigateur ne le dit pas. */
export function schemaSysteme(): 'light' | 'dark' | null {
  try {
    if (typeof globalThis.matchMedia !== 'function') return null;
    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return null;
  }
}

/**
 * Pose le mode résolu sur `<html>`. Le DOM ne porte jamais `systeme` : les
 * sélecteurs CSS n'ont donc qu'un seul cas à connaître.
 */
export function appliquerMode(mode: ModeApparence): ModeResolu {
  const resolu = resoudreMode(mode, schemaSysteme());
  const racine = globalThis.document?.documentElement;
  if (racine) racine.setAttribute('data-apparence', resolu);
  return resolu;
}

/**
 * Applique la préférence enregistrée et s'abonne aux changements du système.
 * Appelé une fois depuis `main.tsx`, AVANT le premier rendu React.
 *
 * @returns une fonction de désabonnement (utile en test ; l'application, elle,
 * garde l'abonnement pour toute la vie de l'onglet).
 */
export function initialiserApparence(): () => void {
  appliquerMode(lireMode());
  try {
    if (typeof globalThis.matchMedia !== 'function') return () => undefined;
    const requete = globalThis.matchMedia('(prefers-color-scheme: dark)');
    // Le système peut basculer PENDANT la session (coucher du soleil sur macOS,
    // horaire nocturne sur Windows). En mode « Automatique », la console suit.
    const surChangement = (): void => {
      if (lireMode() === 'systeme') appliquerMode('systeme');
    };
    requete.addEventListener('change', surChangement);
    return () => requete.removeEventListener('change', surChangement);
  } catch {
    return () => undefined;
  }
}
